import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import debug from 'debug';
import { RootState } from '../state/configureStore';
import { setLanguage, setLoadingLanguage } from '../state/actions/ui/language';

const log = debug('rois:hooks:language');

if (import.meta.env.DEV) {
  debug.enable('rois:hooks:language*');
}

export const useLanguage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { availableLanguages, isLoading } = useSelector(
    (state: RootState) =>
      state.language || {
        availableLanguages: ['ca', 'es', 'en'],
        isLoading: false,
      }
  );

  // Usar el idioma actual de i18next y normalizar (quitar región)
  const rawLanguage = i18n.language || 'es';
  const currentLanguage = rawLanguage.split('-')[0]; // es-ES -> es

  // 🔍 LOGS PARA DEBUG
  log('🔧 useLanguage Debug:', {
    'i18n.language': i18n.language,
    'i18n.resolvedLanguage': i18n.resolvedLanguage,
    'i18n.languages': i18n.languages,
    rawLanguage,
    currentLanguage,
    availableLanguages,
  });

  const changeLanguage = useCallback(
    async (lng: string) => {
      if (lng === currentLanguage) return;

      dispatch(setLoadingLanguage(true));
      try {
        // Forzar recarga de todos los namespaces para el nuevo idioma
        const namespaces = [
          'common',
          'auth',
          'navigation',
          'forms',
          'pages',
          'errors',
          'tables',
          'buttons',
          'modals',
          'toasts',
          'validation',
          'campaigns',
          'users',
          'workOrders',
          'communications',
          'accidents',
          'multimedia',
        ];

        // Recargar todos los recursos para el nuevo idioma
        await Promise.all(
          namespaces.map((ns) => i18n.reloadResources(lng, ns))
        );

        // Cambiar el idioma
        await i18n.changeLanguage(lng);
        dispatch(setLanguage(lng));

        // Actualizar el HTML lang attribute
        document.documentElement.lang = lng;

        // Guardar en localStorage
        localStorage.setItem('rois-language', lng);

        log('🔄 Language changed and resources reloaded:', lng);
      } catch (error) {
        console.error('Error changing language:', error);
      } finally {
        dispatch(setLoadingLanguage(false));
      }
    },
    [currentLanguage, dispatch, i18n]
  );

  const getLanguageName = useCallback((lng: string) => {
    const languageNames: Record<string, string> = {
      ca: 'Català',
      es: 'Español',
      en: 'English',
    };
    return languageNames[lng] || lng;
  }, []);

  const getLanguageFlag = useCallback((lng: string) => {
    const flags: Record<string, string> = {
      ca: '🏴󠁥󠁳󠁣󠁴󠁿', // Cataluña
      es: '🇪🇸', // España
      en: '🇬🇧', // Reino Unido
    };
    return flags[lng] || '🌐';
  }, []);

  return {
    // Función de traducción
    t,

    // Estado del idioma
    currentLanguage,
    availableLanguages,
    isLoading,

    // Funciones
    changeLanguage,
    getLanguageName,
    getLanguageFlag,

    // Instancia de i18n para casos avanzados
    i18n,
  };
};
